import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Button from '@ui/Button';
import { useLayoutContext } from '../../../../BaseLayout/context/LayoutContext';

export const ALL_TERMS = 'a-z';
export const NUMERICAL_TERMS = '#';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    [theme.breakpoints.down(707)]: {
      justifyContent: 'start',
    },
  },
  /**
   * Based on dynamic width if left/right rail is open,
   * let items wrap at the start of the next row instead of
   * evenly spaced to take up entire container
   *
   * Otherwise items in new row are spaced too widely in the center
   */
  withLeftRight: {
    [theme.breakpoints.down(1162)]: {
      justifyContent: 'start',
    },
  },
  withLeftOnly: {
    [theme.breakpoints.down(890)]: {
      justifyContent: 'start',
    },
  },
  withRightOnly: {
    [theme.breakpoints.down(978)]: {
      justifyContent: 'start',
    },
  },
}));

const useTextButtonStyles = makeStyles()(theme => ({
  text: {
    minWidth: theme.spacing(2),
    color: theme.palette.grey[500],
  },
  selected: {
    color: theme.palette.primary.main,
  },
}));

interface BaseSortToolbarButtonProps
  extends Pick<ReplacementsLibraryToolbarProps, 'onChange'> {
  term: string;
  selected: boolean;
}

const BaseSortToolbarTextButton = (props: BaseSortToolbarButtonProps) => {
  const { term, selected, onChange } = props;
  const { classes, cx } = useTextButtonStyles();
  return (
    <Button
      variant="text"
      classes={{
        root: cx(classes.text, {
          [classes.selected]: selected,
        }),
      }}
      size="small"
      color="primary"
      onClick={() => onChange(term)}
    >
      {term}
    </Button>
  );
};

const SortToolbarTextButton = React.memo(
  BaseSortToolbarTextButton,
  (prevProps, nextProps) => {
    return (
      prevProps.selected === nextProps.selected &&
      prevProps.term === nextProps.term
    );
  }
);

interface ReplacementsLibraryToolbarProps {
  selectedTerm: string;
  onChange: (sortBy: string) => void;
}

const ReplacementsLibrarySortToolbar = (
  props: ReplacementsLibraryToolbarProps
) => {
  const { selectedTerm, onChange } = props;

  const prefix = [ALL_TERMS, NUMERICAL_TERMS];
  const alphabet = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(97 + i)
  );
  const sortTerms = prefix.concat(alphabet);

  const { leftRailVisibility, rightRailVisibility } = useLayoutContext();

  const isLeftRailClosed = leftRailVisibility === 'closed';
  const isRightRailClosed = rightRailVisibility === 'closed';

  const { classes, cx } = useStyles();
  return (
    <Box
      className={cx(classes.container, {
        [classes.withLeftRight]: !isLeftRailClosed && !isRightRailClosed,
        [classes.withLeftOnly]: !isLeftRailClosed && isRightRailClosed,
        [classes.withRightOnly]: !isRightRailClosed && isLeftRailClosed,
      })}
    >
      {sortTerms.map(term => (
        <SortToolbarTextButton
          key={`sort-${term}`}
          term={term}
          selected={selectedTerm === term}
          onChange={onChange}
        />
      ))}
    </Box>
  );
};

export default ReplacementsLibrarySortToolbar;
